import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findFirstComponentOfType } from "../internal/TextExtractor";

interface FacadeProps {
  children?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}

/**
 * One-way still→payload swap. Until activation, `arg-content` is not mounted
 * so embeds are not requested on SSR or first paint.
 */
export function Facade({ children, className, "aria-label": ariaLabel }: FacadeProps): React.ReactElement {
  const [revealed, setRevealed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasFocusedContentRef = useRef(false);

  const imageChild = useMemo(() => findFirstComponentOfType(children, "arg-image"), [children]);
  const contentChild = useMemo(() => findFirstComponentOfType(children, "arg-content"), [children]);

  const reveal = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
    setRevealed(true);
  }, []);

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    reveal(event);
  }, [reveal]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      if (event.key === " ") {
        event.preventDefault();
      }
      reveal(event);
    }
  }, [reveal]);

  useEffect(() => {
    if (!revealed) {
      return;
    }
    if (hasFocusedContentRef.current) {
      return;
    }
    const node = contentRef.current;
    if (!node) {
      return;
    }
    hasFocusedContentRef.current = true;
    node.focus();
  }, [revealed]);

  if (!imageChild) {
    return <></>;
  }

  const outerClassName = ["_facade", revealed ? "_facade-revealed" : "_facade-unrevealed", className]
    .filter(Boolean)
    .join(" ");

  if (revealed) {
    return (
      <div className={outerClassName}>
        <div className="_facade-content" tabIndex={-1} ref={contentRef}>
          {contentChild?.props.children}
        </div>
      </div>
    );
  }

  return (
    <div className={outerClassName}>
      <div
        className="_facade-image"
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {imageChild.props.children}
      </div>
    </div>
  );
}
